'use client';

import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import Checkbox from '@publishing/components/forms/checkbox/Checkbox';
import FormMessage from '@publishing/components/forms/_FormMessage';
import Icon from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';
import AgreementModal from './AgreementModal';
import styles from './AgreementBox.module.scss';
import { AGREEMENT_BODY_COPY, type LeadAgreementId } from '../agreementCopy';
import type { FormAgreementLang } from '../_types';

import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

interface AgreementItemCopy {
  id: string;
  label: string;
}

interface AgreementLocaleCopy {
  agreements: AgreementItemCopy[];
  allAgreeLabel: string;
  requiredBadge: string;
  optionalBadge: string;
  errorMessage: string;
}

/** intro 조각 사이 줄바꿈 — 첫 번째는 PC 에서만, 두 번째는 MO 에서만 숨긴다. */
const INTRO_BREAK_CLASSES = ['desktop:hidden', 'hidden desktop:block'];

const AGREEMENT_I18N: Record<FormAgreementLang, AgreementLocaleCopy> = {
  ko: {
    agreements: [
      { id: 'isPrivacy', label: '개인정보 수집 및 이용 동의' },
      { id: 'isMarketing', label: '마케팅 정보 수집 및 이용 동의' },
    ],
    allAgreeLabel: '전체 동의',
    requiredBadge: '[필수]',
    optionalBadge: '[선택]',
    errorMessage: '필수 동의에 체크해주세요.',
  },
  en: {
    agreements: [
      { id: 'isPrivacy', label: 'Privacy Policy' },
      { id: 'isMarketing', label: 'Marketing Updates' },
    ],
    allAgreeLabel: 'Agree to all',
    requiredBadge: '[Required]',
    optionalBadge: '[Optional]',
    errorMessage: 'Please check all required agreements.',
  },
};

export interface AgreementBoxProps {
  lang?: FormAgreementLang;
  /** 필수 약관 id 목록. 기본값 ['isPrivacy'] */
  required?: string[];
  /** 제출 시도 시 에러 강제 표시 */
  forceShowError?: boolean;
  onAgreedChange?: (agreed: Record<string, boolean>) => void;
  className?: string;
  /** 레이아웃 변형. 'main'이면 하단 여백(margin-bottom)을 띄움 */
  variant?: 'default' | 'main';
}

export default function AgreementBox({
  lang = 'ko',
  required = ['isPrivacy'],
  forceShowError = false,
  onAgreedChange,
  className,
  variant = 'default',
}: AgreementBoxProps) {
  const copy = AGREEMENT_I18N[lang] ?? AGREEMENT_I18N.ko;
  const agreements = copy.agreements;

  const [agreed, setAgreed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(agreements.map((a) => [a.id, true])),
  );
  const [touched, setTouched] = useState(false);
  const [openModalId, setOpenModalId] = useState<string | null>(null);

  // lang 변경 시 약관 id에 맞춰 agreed 키 재구성 (이전 선택값 보존). effect 대신 렌더 중 동기화.
  const [prevLang, setPrevLang] = useState(lang);
  if (prevLang !== lang) {
    setPrevLang(lang);
    const ids = AGREEMENT_I18N[lang]?.agreements ?? AGREEMENT_I18N.ko.agreements;
    setAgreed((prev) => {
      const next: Record<string, boolean> = {};
      for (const a of ids) next[a.id] = prev[a.id] ?? true;
      return next;
    });
  }

  useEffect(() => {
    onAgreedChange?.(agreed);
  }, [agreed, onAgreedChange]);

  const hasAllRequired = required.every((id) => agreed[id]);
  const isAllAgreed = agreements.every((a) => agreed[a.id]);
  const showError = (touched || forceShowError) && !hasAllRequired;

  const toggle = (id: string, checked: boolean) => {
    setTouched(true);
    setAgreed((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleAll = (checked: boolean) => {
    setTouched(true);
    setAgreed(Object.fromEntries(agreements.map((a) => [a.id, checked])));
  };

  const openContent = openModalId
    ? (AGREEMENT_BODY_COPY[lang] ?? AGREEMENT_BODY_COPY.ko)[
        openModalId as LeadAgreementId
      ]
    : null;

  return (
    <>
      <div
        className={clsx(styles.agreement_box, variant === 'main' && styles.spaced, className)}
      >
        <Checkbox
          checked={isAllAgreed}
          onChange={toggleAll}
          label={copy.allAgreeLabel}
          className={styles.all_agree}
          labelClassName={styles.all_agree_label}
        />

        <ul className={styles.agreement_list}>
          {agreements.map((agreement) => (
            <li key={agreement.id} className={styles.agreement_item}>
              <Checkbox
                variant="minimal"
                name={agreement.id}
                checked={agreed[agreement.id]}
                onChange={(checked) => toggle(agreement.id, checked)}
              />
              <button
                type="button"
                className={styles.agreement_button}
                onClick={() => setOpenModalId(agreement.id)}
                {...gtmClick(GTM_EVENT.POPUP_OPEN, '약관 상세')}
              >
                <span className={styles.agreement_text}>
                  <em>{required.includes(agreement.id) ? copy.requiredBadge : copy.optionalBadge}</em>{' '}
                  {agreement.label}
                </span>
                <Icon name={ArrowChevron} direction="right" size={16} className={styles.chevron} />
              </button>
            </li>
          ))}
        </ul>

        <FormMessage errorMessage={showError ? copy.errorMessage : undefined} />
      </div>

      {openContent && (
        <AgreementModal title={openContent.title} onClose={() => setOpenModalId(null)}>
          <p>
            {openContent.intro.map((segment, idx) => (
              <Fragment key={segment}>
                {idx > 0 && (
                  <>
                    <br className={INTRO_BREAK_CLASSES[(idx - 1) % INTRO_BREAK_CLASSES.length]} />{' '}
                  </>
                )}
                {segment}
              </Fragment>
            ))}
          </p>
          <section>
            <ol>
              {openContent.sections.map((section) => (
                <li key={section.heading}>
                  {section.heading}
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </section>
          <ul className={styles.info}>
            {openContent.notes.map((note) => (
              <li key={'text' in note ? note.text : note.link.label}>
                {'text' in note ? (
                  note.text
                ) : (
                  <>
                    {note.before}
                    {/* 폼 안에서 현재 탭을 이동시키면 작성 중이던 입력이 통째로 사라진다. */}
                    <a
                      href={note.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {note.link.label}
                    </a>
                    {note.after}
                  </>
                )}
              </li>
            ))}
          </ul>
        </AgreementModal>
      )}
    </>
  );
}
